import { useDebounce, useI18N } from "@sundae/react-hooks";
import {
  AvatarGroup,
  Balance,
  FlexList,
  FlexListItem,
  InfoCircledIcon,
  Percentage,
  Separator,
  Text,
  Tooltip,
  toolkitCx,
} from "@sundae/ui-toolkit";
import { AssetAmount } from "@sundaeswap/asset";
import { SundaeUtils } from "@sundaeswap/core/utilities";
import isEqual from "lodash/isEqual";
import { FC, useEffect, useMemo } from "react";
import { useCustomCompareMemo } from "use-custom-compare";

import AssetAvatar from "../../../../components/AssetAvatar";
import BalanceWithAssetName from "../../../../components/BalanceWithAssetName";
import LiquidityFeesWrapper from "../../../../components/LiquidityComponents/LiquidityFeesWrapper";
import { INPUT_DEBOUNCE_IN_MS } from "../../../../constants/page.constants";
import { useZapTx } from "../../../../hooks/liquidity/useZapTx";
import { useAppContext, useProfileAppContext } from "../../../../stores";
import { useZapContext } from "../../../../stores/LiquidityContext/ZapContext/context";
import { getAssetName } from "../../../../utils/assets.utils";
import { getPriceImpactStatus } from "../../../../utils/exchange.utils";
import { divideBy, numberI18n, percentageI18n } from "../../../../utils/number-format";
import { getPoolName } from "../../../../utils/pool.utils";

const ZapTransactionDetails: FC = () => {
  const {
    state: { activeZapPool, activeZapAsset },
  } = useAppContext();
  const {
    derived: { zapAssetAmountExceedsBalance },
    state: { swapOutcome, liquidity, aPerB, bPerA, debouncedInputValue, debouncedSwapOutcome },
  } = useZapContext();
  const oppositeAsset = useMemo(
    () =>
      SundaeUtils.isAssetIdsEqual(
        activeZapAsset?.metadata.assetId ?? "",
        activeZapPool?.assetA.assetId ?? ""
      )
        ? activeZapPool?.assetB
        : activeZapPool?.assetA,
    [activeZapPool, activeZapAsset]
  );
  const { t, currentLocale } = useI18N("liquidity");
  const {
    state: { privacyEnabled },
  } = useProfileAppContext();
  const { buildZapTx } = useZapTx();
  const debouncedZapAssetAmountExceedsBalance = useDebounce(zapAssetAmountExceedsBalance, {
    wait: INPUT_DEBOUNCE_IN_MS,
  });

  const depositedActiveAsset = useMemo(
    () => Number(liquidity?.actualDepositedA ?? 0),
    [liquidity?.actualDepositedA]
  );
  const depositedOppositeAsset = useMemo(
    () => Number(liquidity?.actualDepositedB ?? 0),
    [liquidity?.actualDepositedB]
  );

  const transactionSummary = useCustomCompareMemo(
    () => [
      {
        id: "1",
        label: t("confirmationModal.depositedZap", {
          asset: activeZapAsset && getAssetName(activeZapAsset.metadata),
        }),
        value: numberI18n(
          new AssetAmount(
            depositedActiveAsset,
            activeZapAsset?.metadata.decimals
          )?.value.toNumber() ?? 0,
          {
            maximumFractionDigits: activeZapAsset?.metadata.decimals,
          },
          currentLocale
        ),
      },
      {
        id: "2",
        label: t("confirmationModal.depositedZap", {
          asset: oppositeAsset && getAssetName(oppositeAsset),
        }),
        value: numberI18n(
          new AssetAmount(depositedOppositeAsset, oppositeAsset?.decimals)?.value.toNumber() ?? 0,
          {
            maximumFractionDigits: oppositeAsset?.decimals,
          },
          currentLocale
        ),
      },
      {
        id: "3",
        label: (
          <Text className="flex items-center gap-1" tag="span" size="sm" variant="muted">
            {t("txOverview.priceImpact", { ns: "exchange" })}
            <Tooltip
              trigger={
                <div className="cursor-help">
                  <InfoCircledIcon className={toolkitCx.layout.text.muted} />
                </div>
              }
              tooltipLabel={t("txOverview.priceImpact", { context: "tooltip", ns: "exchange" })}
            />
          </Text>
        ),
        value: (
          <Percentage
            abbreviate
            locale={currentLocale}
            size="sm"
            value={swapOutcome?.priceImpact.abs().toNumber() ?? 0}
            variant={getPriceImpactStatus(swapOutcome?.priceImpact.abs().toNumber())}
          />
        ),
      },
      {
        id: "4",
        value: activeZapPool && percentageI18n(divideBy(activeZapPool.fee), currentLocale),
        label: (
          <Text className="flex items-center gap-1" tag="span" size="sm" variant="muted">
            {t("fee", { context: "lp", ns: "fees" })}
            <Tooltip
              trigger={
                <div className="cursor-help">
                  <InfoCircledIcon className={toolkitCx.layout.text.muted} />
                </div>
              }
              tooltipLabel={t("fee", { context: "lpExplained", ns: "fees" })}
            />
          </Text>
        ),
      },
      {
        id: "5",
        label: t("share", { context: "pool", ns: "common" }),
        value: (
          <Percentage
            abbreviate
            locale={currentLocale}
            size="sm"
            value={liquidity?.shareAfterDeposit.toString() ?? "0"}
          />
        ),
      },
    ],
    [
      swapOutcome,
      depositedActiveAsset,
      depositedOppositeAsset,
      activeZapPool,
      activeZapAsset,
      oppositeAsset,
      liquidity,
      currentLocale,
    ],
    isEqual
  );

  const receivedLpTokensSummary = useCustomCompareMemo(
    () => {
      if (liquidity?.generatedLp) {
        return {
          id: "7",
          label: t("lpTokensYouWillReceive"),
          value: (
            <Balance
              abbreviate={false}
              gradient
              locale={currentLocale}
              privacy={privacyEnabled}
              showSymbol={false}
              size="sm"
              value={new AssetAmount(liquidity?.generatedLp)}
            />
          ),
        };
      }

      return undefined;
    },
    [liquidity?.generatedLp, currentLocale, privacyEnabled, t],
    isEqual
  );

  const assetChangeSummary = useCustomCompareMemo(
    () => {
      if (!!liquidity?.aChange) {
        return {
          id: "8",
          label: (
            <>
              <Text
                className="inline-flex items-center justify-center gap-4"
                size="sm"
                variant="muted"
                tag="span"
              >
                {t("zap.change", {
                  asset: activeZapAsset && getAssetName(activeZapAsset.metadata),
                })}
              </Text>
              <Tooltip
                trigger={
                  <div className="cursor-help">
                    <InfoCircledIcon className={toolkitCx.layout.text.muted} />
                  </div>
                }
                tooltipLabel={t("zap.change", { context: "tooltip" })}
              />
            </>
          ),
          value: numberI18n(
            new AssetAmount(
              liquidity.aChange,
              activeZapAsset?.metadata?.decimals
            )?.value.toNumber() ?? 0,
            {
              maximumFractionDigits: activeZapAsset?.metadata?.decimals,
            },
            currentLocale
          ),
        };
      }

      if (!!liquidity?.bChange) {
        return {
          id: "8",
          label: (
            <>
              <Text
                className="inline-flex items-center justify-center gap-4"
                size="sm"
                variant="muted"
                tag="span"
              >
                {t("zap.change", {
                  asset: oppositeAsset && getAssetName(oppositeAsset),
                })}
              </Text>
              <Tooltip
                trigger={
                  <div className="cursor-help">
                    <InfoCircledIcon className={toolkitCx.layout.text.muted} />
                  </div>
                }
                tooltipLabel={t("zap.change", { context: "tooltip" })}
              />
            </>
          ),
          value: numberI18n(
            new AssetAmount(liquidity.bChange, oppositeAsset?.decimals)?.value.toNumber() ?? 0,
            {
              maximumFractionDigits: oppositeAsset?.decimals,
            },
            currentLocale
          ),
        };
      }

      return undefined;
    },
    [liquidity, activeZapAsset, oppositeAsset, currentLocale, t],
    isEqual
  );

  /**
   * Build Tx on debounced `swapOutcome` change.
   */
  useEffect(() => {
    if (
      debouncedSwapOutcome &&
      !debouncedZapAssetAmountExceedsBalance &&
      debouncedInputValue !== "0" &&
      !!debouncedInputValue
    ) {
      buildZapTx();
    }
  }, [debouncedInputValue, debouncedSwapOutcome, debouncedZapAssetAmountExceedsBalance]);

  return (
    <>
      <Separator orientation="horizontal" className={toolkitCx.layout.background.separator} />
      <LiquidityFeesWrapper
        collapseLabel={t("zap.confirm.labels.hideTxDetails")}
        expandLabel={t("zap.confirm.labels.showTxDetails")}
      >
        <FlexList>
          {/* -------------------------------------------------------------------------------------------------
           * Pool
           * -----------------------------------------------------------------------------------------------*/}
          <FlexListItem
            field={{
              label: t("pool", { ns: "glossary" }),
              size: "sm",
              variant: "muted",
            }}
            value={{
              label: (
                <div className="mt-1 flex items-center justify-center gap-1">
                  {activeZapPool && (
                    <>
                      <AvatarGroup>
                        <AssetAvatar asset={activeZapPool.assetA} size="xs" stacked />
                        <AssetAvatar asset={activeZapPool.assetB} size="xs" stacked />
                      </AvatarGroup>

                      <Text tag="span" size="sm">
                        {getPoolName({
                          assetA: activeZapPool.assetA,
                          assetB: activeZapPool.assetB,
                        })}
                      </Text>
                    </>
                  )}
                </div>
              ),
            }}
          />

          {/* -------------------------------------------------------------------------------------------------
           * Rates
           * -----------------------------------------------------------------------------------------------*/}
          <FlexListItem
            className="!items-start"
            field={{
              label: t("rates", { ns: "common" }),
              size: "sm",
              variant: "muted",
            }}
            value={{
              label: (
                <div className="grid gap-1">
                  {[
                    {
                      input: activeZapPool?.assetB,
                      output: activeZapPool?.assetA,
                      parity: aPerB,
                    },
                    {
                      input: activeZapPool?.assetA,
                      output: activeZapPool?.assetB,
                      parity: bPerA,
                    },
                  ].map(({ input, output, parity }, index) => (
                    <div key={`${parity}-${index}`} className="flex items-center justify-end gap-1">
                      <BalanceWithAssetName
                        abbreviate={false}
                        asset={input}
                        locale={currentLocale}
                        showSymbol={!!input && SundaeUtils.isAdaAsset(input)}
                        size="sm"
                        value={AssetAmount.fromValue(1)}
                      />
                      <Text tag="span" size="sm">
                        =
                      </Text>
                      <BalanceWithAssetName
                        abbreviate={false}
                        asset={output}
                        locale={currentLocale}
                        showSymbol={!!output && SundaeUtils.isAdaAsset(output)}
                        size="sm"
                        value={AssetAmount.fromValue(parity ?? "0", output)}
                      />
                    </div>
                  ))}
                </div>
              ),
            }}
          />

          {/* -------------------------------------------------------------------------------------------------
           * Summary
           * -----------------------------------------------------------------------------------------------*/}
          {transactionSummary.map(({ id, value, label }) => (
            <FlexListItem
              key={id}
              field={{
                label,
                size: "sm",
                variant: "muted",
              }}
              value={{
                label: (
                  <Text size="sm" tag="span">
                    {value}
                  </Text>
                ),
              }}
            />
          ))}

          {receivedLpTokensSummary && (
            <FlexListItem
              key={receivedLpTokensSummary.id}
              field={{
                label: receivedLpTokensSummary.label,
                size: "sm",
                variant: "muted",
              }}
              value={{
                label: receivedLpTokensSummary.value,
              }}
            />
          )}

          {assetChangeSummary && (
            <FlexListItem
              key={assetChangeSummary.id}
              field={{
                label: assetChangeSummary.label,
                size: "sm",
                variant: "muted",
              }}
              value={{
                size: "sm",
                variant: "normal",
                label: assetChangeSummary.value,
              }}
            />
          )}
        </FlexList>
      </LiquidityFeesWrapper>
    </>
  );
};

export default ZapTransactionDetails;
